import { IField } from '@eventr/common'
import { Loading, Typography } from '@eventr/common-storybook'
import { FC } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { IEditComponent } from '../../data/Data'
import { bs, useBoundStore } from '../../state/bound.store'
import { AddressEditField } from './editComponents/addressLookup'
import { AirportSelector } from './editComponents/airportSelector'
import { ArrayFinderEdit } from './editComponents/arrayFinder'
import { Attendees } from './editComponents/attendees'
import { AttendeesList } from './editComponents/attendeesList'
import { DateEditField } from './editComponents/datePicker'
import { DateRangePicker } from './editComponents/dateRange'
import { DateTimeEditField } from './editComponents/dateTime'
import { DocumentUploadEditField } from './editComponents/documentUpload'
import { EditMembersItem } from './editComponents/EditMembersItem'
import { GetFlightData } from './editComponents/getFlightData'
import { GroupField } from './editComponents/group'
import { ImageEditField } from './editComponents/imageSelection'
import { JoinLeaveButton } from './editComponents/joinLeaveButton'
import { SectionEdit } from './editComponents/section'
import { SwitchEditField } from './editComponents/switch'
import { TelephoneNumberEdit } from './editComponents/telephoneNumber'
import { TextEditField } from './editComponents/textInput'
import { TimeEditField } from './editComponents/time'
import { TimezoneSelector } from './editComponents/timezoneSelector'
import { UserSelectionField } from './editComponents/userSelection'

type props = {
    _id: string
    editComponent?: IEditComponent
}
export const ComponentFinderEdit: FC<props> = ({ _id, editComponent }) => {
    const { fields } = useBoundStore(
        useShallow((s) => {
            return {
                fields: s.fields,
            }
        })
    )

    if (fields.length === 0) {
        return <Loading />
    }
    if (editComponent?.type === 'field') {
        const fieldInfo = fields.find((field: IField) => {
            return field.id.toString() === editComponent?.fieldId?.toString()
        })
        if (!fieldInfo || !isVisible(fieldInfo)) {
            return null as React.ReactElement | null
        }

        switch (fieldInfo?.field_type) {
            case 'select': {
                if (editComponent.settings?.inLine) {
                    return (
                        <EditMembersItem
                            field={fieldInfo}
                            settings={editComponent.settings}
                            _id={_id}
                        />
                    )
                }
                return (
                    <UserSelectionField
                        field={fieldInfo}
                        _id={_id}
                        editComponent={editComponent}
                    />
                )
            }
            case 'text': {
                return <TextEditField field={fieldInfo} _id={_id} />
            }
            case 'date': {
                return <DateEditField field={fieldInfo} _id={_id} />
            }
            case 'dateRange': {
                return <DateRangePicker field={fieldInfo} _id={_id} />
            }
            case 'time': {
                return <TimeEditField field={fieldInfo} _id={_id} />
            }
            case 'addressLookup': {
                return (
                    <AddressEditField
                        field={fieldInfo}
                        _id={_id}
                        settings={editComponent}
                    />
                )
            }
            case 'imageSelection': {
                return <ImageEditField field={fieldInfo} _id={_id} />
            }
            case 'flightButton': {
                return <GetFlightData field={fieldInfo} _id={_id} />
            }
            case 'uploadDocument': {
                return <DocumentUploadEditField field={fieldInfo} _id={_id} />
            }
            case 'group': {
                return (
                    <GroupField
                        field={fieldInfo}
                        _id={_id}
                        options={editComponent}
                    />
                )
            }
            case 'dateTime': {
                return <DateTimeEditField field={fieldInfo} _id={_id} />
            }
            case 'switch': {
                return <SwitchEditField field={fieldInfo} _id={_id} />
            }
            /// HARDCODED FOR USERS
            case 'list': {
                return <AttendeesList field={fieldInfo} _id={_id} />
            }
            case 'telephoneNumber': {
                return <TelephoneNumberEdit field={fieldInfo} _id={_id} />
            }
            case 'airportSelector': {
                return <AirportSelector field={fieldInfo} _id={_id} />
            }
            case 'timezoneSelector': {
                return <TimezoneSelector field={fieldInfo} _id={_id} />
            }
            default: {
                return <Typography />
            }
        }
    }
    if (editComponent?.type === 'section') {
        return <SectionEdit options={editComponent} _id={_id} />
    }
    if (editComponent?.type === 'arrayFinder') {
        const fieldInfo = fields.find((field: IField) => {
            return field.id.toString() === editComponent.fieldId?.toString()
        })

        if (fieldInfo) {
            return (
                <ArrayFinderEdit
                    field={fieldInfo}
                    _id={_id}
                    options={editComponent}
                />
            )
        }
        return null
    }
    if (editComponent?.type === 'attendees') {
        return <Attendees options={editComponent} _id={_id} />
    }
    if (editComponent?.type === 'joinLeaveButton') {
        return <JoinLeaveButton options={editComponent} _id={_id} />
    }
}

const isVisible = (field: IField) => {
    if (!field) return false
    if (
        field.type_properties?.OrgTypeVisibility &&
        field.type_properties?.OrgTypeVisibility.length > 0
    ) {
        return field.type_properties.OrgTypeVisibility?.includes(
            bs().tier ?? ''
        )
    }
    return true
}
